import { CSSProperties, useEffect, useState } from "react";
import { InputAdornment, TextField, styled } from "@mui/material";

import { BORDER_COLORS } from "../../constants";
import { toNumber } from "../../helpers/formatter";

export const BootstrapInput = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    paddingLeft: "0px",
    backgroundColor: theme.palette.mode === "light" ? "#FFFFFF" : "#2b2b2b",
    paddingRight: "0px",
    height: "100%",
    width: "100%",
    "& input": {
      padding: "4px 8px",
      fontSize: "14px",
      color: "#171A1F",
      height: "100%",
    },
    "& .MuiInputAdornment-root": {
      padding: "0px 4px",
      height: "100%",
      maxHeight: "100%",
      margin: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "transparent",
      "p.MuiTypography-root": {
        fontSize: "14px",
        lineHeight: "14px",
        color: "#565D6D",
        fontWeight: 500,
      },
    },
    "& .MuiInputAdornment-positionStart": {
      padding: "0px 8px 0px 8px",
    },
    "& .MuiInputAdornment-positionEnd": {
      padding: "0px 8px 0px 8px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_COLORS.default,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_COLORS.focused,
      borderWidth: "1px",
      boxShadow: "none",
      fontSize: "16px",
      fontWeight: "400",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#DCDDDD",
    borderWidth: "1px",
  },
  "& .MuiInputLabel-outlined": {
    "&.Mui-focused": {
      color: BORDER_COLORS.focused,
    },
  },
  "& .MuiFormHelperText-root": {
    margin: "4px 0 0 0",
    lineHeight: 1.3,
  },
}));

interface IDecimalInputProps {
  prefix?: string;
  suffix?: string;
  value?: string | number;
  align?: string;
  type?: string;
  readOnly?: boolean;
  style?: CSSProperties;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  error?: boolean;
  helperText?: string;
  onChange?: (val: number) => void;
  onEditing?: (val: number) => void;
}

export const DecimalInput: React.FC<IDecimalInputProps> = ({
  type = "number",
  value: defaultValue,
  prefix,
  suffix,
  align = "left",
  size = "lg",
  min = 0,
  max,
  step = 0.01,
  onChange,
  onEditing,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [value, setValue] = useState<string | number | undefined>(defaultValue);
  const digits = Math.log10(1 / step);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    event.target.select();
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (type === "number") {
      let val: number = toNumber(value);
      val = Math.max(min, val);
      val = max ? Math.min(val, max) : val;
      val = toNumber(val.toFixed(digits));
      if (val !== toNumber(value)) {
        setValue(val);
        onEditing?.(val);
      }
      if (val !== toNumber(defaultValue)) {
        onChange?.(val);
      }
    }
  };

  return (
    <BootstrapInput
      {...props}
      type={type}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: "34px",
          "& input": {
            textAlign: align,
          },
        },
      }}
      value={
        isFocused
          ? value
          : type === "number"
            ? toNumber(value).toFixed(digits)
            : value
      }
      slotProps={{
        input: {
          startAdornment: prefix != undefined && (
            <InputAdornment position="start">{prefix}</InputAdornment>
          ),
          endAdornment: suffix !== undefined && (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ),
        },
        htmlInput: {
          step,
          min,
          max,
        },
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        onEditing?.(toNumber(e.target.value));
      }}
    />
  );
};
